// lightning-unified-service.ts — coordinator for the single worldwide "Lightning"
// layer. It does NOT render anything itself; it picks the highest-fidelity source
// whose coverage contains the current viewport and drives the existing renderer for
// that source, swapping on pan (exactly like the OM model auto-switch). All three
// sources render onto the shared 0..1 "Lightning Index" (see lightning-scale.ts) so
// the legend is identical regardless of which source is active.
//
// Fidelity ladder (highest available wins):
//   1. HRRR LTNG flash density over CONUS      → nwmLightningUnifiedService (NWM/COG)
//   2. ICON LPI where a model provides it       → OM sub-layer (lightning_potential)
//   3. ICON weather_code worldwide fallback     → OM sub-layer (weather_code)
//
// Each tier is independent, so if the weather_code tier has trouble the HRRR and LPI
// tiers are unaffected. Source selection is data-driven via selectBestModel, so any
// future OM model that adds lightning_potential / weather_code is picked up
// automatically.

import maplibregl from 'maplibre-gl'
import { logger } from '../../utils/logger.js'
import {
  selectBestModel,
  getCandidateModels,
  bboxContainsExtent,
  bboxIntersectsExtent,
  type Bbox,
} from './om-model-selector.js'
import { OM_MODELS_BY_ID, type OmLayerDef } from './om-types.js'
import {
  enableOmLayer,
  disableOmLayer,
  setOmLayerOpacity,
  isOmLayerEnabled,
} from './om-layer-service.js'
import { nwmLightningUnifiedService } from './nwm-service.js'
import { UNIFIED_LIGHTNING } from './om/om-colormap.js'
import { lpiToUnified, weatherCodeToUnified } from './lightning-scale.js'

const log = logger.create('lightning')
const DEFAULT_OPACITY = 0.6
const DEBOUNCE_MS = 300

// HRRR coverage = the OM HRRR model's true bbox (the LCC fan), reused so this CONUS
// tier matches the HRRR extent used elsewhere in the app.
const HRRR_BBOX = OM_MODELS_BY_ID.get('ncep_hrrr_conus')?.bbox ?? null

// Internal OM sub-layer defs — deliberately NOT added to OM_LAYERS, so they never
// appear as standalone toggles. Each renders its variable onto the shared unified
// index (valueTransform + UNIFIED_LIGHTNING colormap). The OM service handles model
// auto-selection within each variable, so new OM lightning models are picked up.
const OM_LPI_LAYER: OmLayerDef = {
  id: 'lightning-om-lpi',
  label: 'Lightning (LPI)',
  variable: 'lightning_potential',
  modelFilter: m => m.variables.has('lightning_potential'),
  colormap: UNIFIED_LIGHTNING,
  valueTransform: lpiToUnified,
}
const OM_WX_LAYER: OmLayerDef = {
  id: 'lightning-om-wx',
  label: 'Lightning (WMO)',
  variable: 'weather_code',
  modelFilter: m => m.variables.has('weather_code'),
  colormap: UNIFIED_LIGHTNING,
  valueTransform: weatherCodeToUnified,
  // Soften the coarse 3-level categorical field into a potential-like gradient (a
  // cell flanked by storm cells gains some value). Only the WMO tier sets this —
  // LPI/HRRR are continuous and must NOT be blurred.
  smoothRadius: 1,
}

type Source = 'hrrr' | 'lpi' | 'wx' | null

// User-facing labels for each source (model name only, matching the Key's style).
const SOURCE_LABELS: Record<Exclude<Source, null>, string> = {
  hrrr: 'HRRR',
  lpi: 'ICON-D2 (LPI)',
  wx: 'ICON Global',
}

export interface LightningSourceOption {
  /** 'auto' or a concrete source id. */
  id: 'auto' | Exclude<Source, null>
  label: string
}

class LightningUnifiedService {
  readonly id = 'lightning-unified'
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private active: Source = null
  private activeLabel = ''
  // User pin: when set, we render this source instead of auto-selecting — but ONLY
  // where it's actually available for the viewport (else we fall back to auto).
  private pinned: Exclude<Source, null> | null = null
  private moveHandler: (() => void) | null = null
  private moveDebounce: ReturnType<typeof setTimeout> | null = null

  get enabled(): boolean {
    return this._enabled
  }

  /** Human-readable name of the currently rendered source (for a fidelity badge). */
  get activeSourceLabel(): string {
    return this.activeLabel
  }

  get opacity(): number {
    return this._opacity
  }

  // Stores the map for viewport queries and registers the HRRR sub-service (which is
  // NOT in ALL_NWM_SERVICES, so the switcher's init loop doesn't register it).
  setMap(map: maplibregl.Map): void {
    this.map = map
    nwmLightningUnifiedService.register(map)
  }

  enable(): void {
    if (this._enabled) return
    this._enabled = true
    this.attachMoveListener()
    this.selectSource()
  }

  disable(): void {
    if (!this._enabled) return
    this._enabled = false
    this.detachMoveListener()
    this.deactivateAllExcept(null)
    this.active = null
    this.activeLabel = ''
  }

  setOpacity(opacity: number): void {
    this._opacity = opacity
    if (this.active === 'hrrr') nwmLightningUnifiedService.setOpacity(opacity)
    else if (this.active === 'lpi') setOmLayerOpacity(OM_LPI_LAYER.id, opacity)
    else if (this.active === 'wx') setOmLayerOpacity(OM_WX_LAYER.id, opacity)
  }

  private viewportExtent(): Bbox | null {
    if (!this.map) return null
    const b = this.map.getBounds()
    return [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()]
  }

  // Sources eligible for AUTO-select: require FULL-viewport containment so the
  // whole view is covered (HRRR fan / an LPI model domain). weather_code (ICON
  // Global) is effectively always available. Matches selectBestModel / OM rasters.
  private autoSources(extent: Bbox): Exclude<Source, null>[] {
    const out: Exclude<Source, null>[] = []
    if (HRRR_BBOX && bboxContainsExtent(HRRR_BBOX, extent)) out.push('hrrr')
    if (selectBestModel(extent, 'lightning_potential')) out.push('lpi')
    if (selectBestModel(extent, 'weather_code')) out.push('wx')
    return out
  }

  // Sources the user may PIN: looser — a source is offered if it INTERSECTS the
  // view (could render at least part of it). So HRRR/LPI stay pickable when the
  // view only partly overlaps their domain, even though auto wouldn't choose them.
  private pinnableSources(extent: Bbox): Exclude<Source, null>[] {
    const out: Exclude<Source, null>[] = []
    if (HRRR_BBOX && bboxIntersectsExtent(HRRR_BBOX, extent)) out.push('hrrr')
    if (getCandidateModels('lightning_potential', extent).length) out.push('lpi')
    if (getCandidateModels('weather_code', extent).length) out.push('wx')
    return out
  }

  // Pick the source to render. A user PIN wins if it can render here at all
  // (intersects the view); otherwise follow the auto ladder (first containment-
  // eligible source, highest fidelity first).
  private pickSource(extent: Bbox): Source {
    if (this.pinned && this.pinnableSources(extent).includes(this.pinned)) return this.pinned
    return this.autoSources(extent)[0] ?? null
  }

  /** Source options the user may pin for the CURRENT viewport: Auto + every source
   *  that intersects the view (so HRRR/LPI stay pickable on partial overlap). */
  getSourceOptions(): LightningSourceOption[] {
    const extent = this.viewportExtent()
    const avail = extent ? this.pinnableSources(extent) : []
    const auto: LightningSourceOption = { id: 'auto', label: 'Auto' }
    return [auto, ...avail.map(s => ({ id: s, label: SOURCE_LABELS[s] }))]
  }

  /** Current pin ('auto' = following the fidelity ladder). */
  get selectedSource(): 'auto' | Exclude<Source, null> {
    return this.pinned ?? 'auto'
  }

  /** Pin a source (or 'auto' to resume auto-select), then re-evaluate. */
  setSource(id: 'auto' | Exclude<Source, null>): void {
    this.pinned = id === 'auto' ? null : id
    this.selectSource()
  }

  private selectSource(): void {
    if (!this._enabled) return
    const extent = this.viewportExtent()
    if (!extent) return
    const source = this.pickSource(extent)
    this.activeLabel = source ? SOURCE_LABELS[source] : ''
    // Same tier: the underlying renderer (OM service / NWM raster source) handles
    // re-windowing and within-variable model reselection on its own move handler.
    if (source === this.active) return
    log.info(`lightning source → ${source ?? 'none'} (${this.activeLabel})`)
    this.active = source
    this.deactivateAllExcept(source)
    this.activate(source)
  }

  private activate(source: Source): void {
    if (source === 'hrrr') {
      if (!nwmLightningUnifiedService.enabled) nwmLightningUnifiedService.enable()
      nwmLightningUnifiedService.setOpacity(this._opacity)
    } else if (source === 'lpi') {
      if (!isOmLayerEnabled(OM_LPI_LAYER.id)) enableOmLayer(OM_LPI_LAYER)
      setOmLayerOpacity(OM_LPI_LAYER.id, this._opacity)
    } else if (source === 'wx') {
      if (!isOmLayerEnabled(OM_WX_LAYER.id)) enableOmLayer(OM_WX_LAYER)
      setOmLayerOpacity(OM_WX_LAYER.id, this._opacity)
    }
  }

  private deactivateAllExcept(keep: Source): void {
    if (keep !== 'hrrr' && nwmLightningUnifiedService.enabled) {
      nwmLightningUnifiedService.disable()
    }
    if (keep !== 'lpi' && isOmLayerEnabled(OM_LPI_LAYER.id)) {
      disableOmLayer(OM_LPI_LAYER.id)
    }
    if (keep !== 'wx' && isOmLayerEnabled(OM_WX_LAYER.id)) {
      disableOmLayer(OM_WX_LAYER.id)
    }
  }

  private attachMoveListener(): void {
    if (this.moveHandler || !this.map) return
    this.moveHandler = () => {
      if (this.moveDebounce) clearTimeout(this.moveDebounce)
      this.moveDebounce = setTimeout(() => this.selectSource(), DEBOUNCE_MS)
    }
    this.map.on('moveend', this.moveHandler)
    this.map.on('zoomend', this.moveHandler)
  }

  private detachMoveListener(): void {
    if (this.moveHandler && this.map) {
      this.map.off('moveend', this.moveHandler)
      this.map.off('zoomend', this.moveHandler)
    }
    this.moveHandler = null
    if (this.moveDebounce) {
      clearTimeout(this.moveDebounce)
      this.moveDebounce = null
    }
  }
}

export const lightningUnifiedService = new LightningUnifiedService()
